import { useCallback, useContext, useState } from "react";
import { FinancialAccount } from "@common/types/financialAccount";
import {
    BankReconciliation,
    ReconciliationSummaryResponse,
    ReconciliationUnmatchedBehavior,
    TransactionForReconciliationFromAccount,
    TransactionsPairForReconciliation,
} from "@common/contracts/reconciliation.contract";
import {
    getStatmentTransactionId,
    pairHasDifference,
} from "@common/helpers/reconciliation";
import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { toUTCDateOnly } from "@common/helpers/date";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { ReconciliationMatchFooter } from "./ReconciliationMatchFooter";
import {
    FocusRequest,
    ReconciliationTransactionsTable,
} from "./ReconciliationTransactionsTable";
import styles from "./Reconciliation.module.scss";
import { ReconciliationContext } from "./Reconciliation.context";
import {
    findNextWrappingIndex,
    getReconciliationPairKey,
    isSamePair,
    pairDifferenceIsAccepted,
    ReconciliationReviewMode,
} from "./helpers";
import { ReconciliationFindTransaction } from "./ReconciliationFindTransaction";
import { useReconciliationBalances } from "./useReconciliationBalances";
import {
    isReconciliationCashItem,
    matchToStatementTransaction,
} from "./pairMatching";

interface Props {
    financialAccount: FinancialAccount;
    previousReconciliation?: BankReconciliation;
    onBack: () => void;
    onHide: () => void;
    onSuccess?: () => void;
    onOpeningBalanceUpdated: () => Promise<void>;
    summary: ReconciliationSummaryResponse;
    setSummary: React.Dispatch<
        React.SetStateAction<ReconciliationSummaryResponse | undefined>
    >;
    mode: ReconciliationReviewMode;
}

export const ReconciliationMatchStep: React.FC<Props> = ({
    onBack,
    onHide,
    onSuccess,
    onOpeningBalanceUpdated,
    financialAccount,
    previousReconciliation,
    summary,
    setSummary,
    mode,
}) => {
    const {
        financialTransactionsToRemoveIdsSet,
        statementTransactionsToAddIdsSet,
        unregisterFinancialTransactionToRemove,
        unregisterStatementTransactionToAdd,
        unregisterNotReconciledFinancialTransaction,
        unregisterFinancialTransactionFromPreviousPeriod,
        unregisterCheckMerge,
        transactionToMatch,
        setTransactionToMatch,
        notReconciledFinancialTransactionsIdsSet,
        financialTransactionsFromPreviousPeriodIds,
        resetReconciliationState,
    } = useContext(ReconciliationContext);
    const pairs = summary.transactionsPairs;
    const {
        statementsCashIn,
        statementsCashOut,
        kickReconciledCashIn,
        kickReconciledCashOut,
        kickUnreconciled,
        kickOpeningBalanceAsOfReconciliationStart,
        kickClosingReconciledBalance,
        kickClosingUnreconciledBalance,
        isBalanced,
    } = useReconciliationBalances({ summary, financialAccount });

    const kickOpeningBalance = summary.kickOpeningBalance;

    const [focusRequest, setFocusRequest] = useState<FocusRequest>();

    const goToNextChange = useCallback(() => {
        const isPendingDifference = (pair: TransactionsPairForReconciliation) =>
            pairHasDifference(pair) &&
            !pairDifferenceIsAccepted({
                pair,
                financialTransactionsToRemoveIdsSet,
                statementTransactionsToAddIdsSet,
                notReconciledFinancialTransactionsIdsSet,
                financialTransactionsFromPreviousPeriodIds,
            });
        const focusedIndex =
            focusRequest == null
                ? -1
                : pairs.findIndex(
                      (pair) =>
                          getReconciliationPairKey(pair) === focusRequest.key,
                  );
        const nextIndex = findNextWrappingIndex(
            pairs,
            focusedIndex,
            isPendingDifference,
        );
        if (nextIndex < 0) {
            return;
        }
        const nextKey = getReconciliationPairKey(pairs[nextIndex]);
        if (nextKey == null) {
            return;
        }
        // Bump `requestId` even when targeting the same pair so the table's
        // scroll-and-focus effect runs on every click — supports the case
        // where the only pending difference is already focused but the user
        // clicks "Go to next difference" again.
        setFocusRequest((prev) => ({
            key: nextKey,
            requestId: (prev?.requestId ?? 0) + 1,
        }));
    }, [
        pairs,
        focusRequest,
        financialTransactionsToRemoveIdsSet,
        statementTransactionsToAddIdsSet,
        notReconciledFinancialTransactionsIdsSet,
        financialTransactionsFromPreviousPeriodIds,
    ]);

    const updateMatch = useCallback(
        (
            statementTransactionId: string,
            financialTransactionId: number,
            pairsToUse?: TransactionsPairForReconciliation[],
        ) => {
            const newPairs = matchToStatementTransaction({
                pairs: pairsToUse ?? pairs,
                statementTransactionId,
                financialTransactionId,
            });
            if (!newPairs) {
                return;
            }

            if (statementTransactionsToAddIdsSet.has(statementTransactionId)) {
                unregisterStatementTransactionToAdd(statementTransactionId);
            }
            if (
                financialTransactionsToRemoveIdsSet.has(financialTransactionId)
            ) {
                unregisterFinancialTransactionToRemove(financialTransactionId);
            }
            unregisterNotReconciledFinancialTransaction(financialTransactionId);
            unregisterFinancialTransactionFromPreviousPeriod(
                financialTransactionId,
            );
            unregisterCheckMerge(financialTransactionId);

            setSummary({ ...summary, transactionsPairs: newPairs });
        },
        [
            summary,
            setSummary,
            pairs,
            statementTransactionsToAddIdsSet,
            financialTransactionsToRemoveIdsSet,
            unregisterStatementTransactionToAdd,
            unregisterFinancialTransactionToRemove,
            unregisterNotReconciledFinancialTransaction,
            unregisterFinancialTransactionFromPreviousPeriod,
            unregisterCheckMerge,
        ],
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over) {
            return;
        }

        updateMatch(over.id as string, active.id as number);
    };

    const handleMatch = useCallback(
        async (transaction: TransactionListItem) => {
            if (!transactionToMatch) {
                return;
            }

            const financialTransactionAlreadyPresent = pairs.some(
                (pair) =>
                    pair.fromAccount?.financialTransactionId === transaction.id,
            );

            if (!financialTransactionAlreadyPresent) {
                const transactionFromAccount: TransactionForReconciliationFromAccount =
                    {
                        financialTransactionId: transaction.id,
                        amount: transaction.amount,
                        date:
                            transaction.dateOnly ??
                            toUTCDateOnly(transaction.date),
                        bankDescription: transaction.bankDescription,
                        unmatchedBehavior:
                            ReconciliationUnmatchedBehavior.IGNORE,
                        isCashItem: isReconciliationCashItem({
                            transaction,
                            summary,
                        }),
                    };

                const newPairs = [...pairs];
                newPairs.push({
                    fromAccount: transactionFromAccount,
                });

                updateMatch(
                    getStatmentTransactionId(
                        transactionToMatch.statementTransactionToMatch,
                    ),
                    transaction.id,
                    newPairs,
                );
            } else {
                updateMatch(
                    getStatmentTransactionId(
                        transactionToMatch.statementTransactionToMatch,
                    ),
                    transaction.id,
                );
            }

            setTransactionToMatch(undefined);
        },
        [
            pairs,
            updateMatch,
            setTransactionToMatch,
            transactionToMatch,
            summary,
        ],
    );

    const updatePair = useCallback(
        (pair: TransactionsPairForReconciliation) => {
            setSummary(
                (prev) =>
                    prev && {
                        ...prev,
                        transactionsPairs: prev.transactionsPairs.map(
                            (checkedPair) =>
                                isSamePair(checkedPair, pair)
                                    ? pair
                                    : checkedPair,
                        ),
                    },
            );
        },
        [setSummary],
    );

    const updateOpeningBalance = useCallback(async () => {
        resetReconciliationState();
        setFocusRequest(undefined);
        await onOpeningBalanceUpdated();
    }, [onOpeningBalanceUpdated, resetReconciliationState]);

    return (
        <div className={styles.workflowContainer}>
            <DndContext onDragEnd={handleDragEnd}>
                <div className={styles.workflowScrollbarsContainer}>
                    <ReconciliationTransactionsTable
                        financialAccount={financialAccount}
                        pairs={pairs}
                        kickOpeningDate={summary.kickOpeningDate}
                        openingDate={summary.statementsOpeningDate}
                        closingDate={summary.statementsClosingDate}
                        statementsOpeningBalance={
                            summary.statementsOpeningBalance
                        }
                        statementsClosingBalance={
                            summary.statementsClosingBalance
                        }
                        statementsCashIn={statementsCashIn}
                        statementsCashOut={statementsCashOut}
                        kickReconciledCashIn={kickReconciledCashIn}
                        kickReconciledCashOut={kickReconciledCashOut}
                        kickUnreconciled={kickUnreconciled}
                        kickOpeningBalance={kickOpeningBalance}
                        kickOpeningBalanceAsOfReconciliationStart={
                            kickOpeningBalanceAsOfReconciliationStart
                        }
                        kickReconciledClosingBalance={
                            kickClosingReconciledBalance
                        }
                        kickUnreconciledClosingBalance={
                            kickClosingUnreconciledBalance
                        }
                        isKickOpeningBalanceEditable={
                            !previousReconciliation &&
                            financialAccount.isBusiness !== false
                        }
                        onUpdatePair={updatePair}
                        onOpeningBalanceUpdated={updateOpeningBalance}
                        isBalanceReconciliation={
                            summary.isBalanceReconciliation
                        }
                        focusRequest={focusRequest}
                    />
                </div>

                <ReconciliationMatchFooter
                    onHide={onHide}
                    onBack={onBack}
                    onSuccess={onSuccess}
                    pairs={pairs}
                    financialAccount={financialAccount}
                    isBalanced={isBalanced}
                    goToNextChange={goToNextChange}
                    mode={mode}
                />
            </DndContext>
            <ReconciliationFindTransaction
                financialAccount={financialAccount}
                onMatch={handleMatch}
                summary={summary}
            />
        </div>
    );
};
